import React from "react";

/**
 * GlobalEducationJourney — "Blueprint" edition
 * Pure React + Tailwind CSS, white background.
 * Design language: a technical drawing / architectural blueprint —
 * fitting for a story about *engineering* an education platform from
 * research gathered across 14 countries.
 */

const countries = [
  { id: "01", flag: "🇫🇷", name: "France" },
  { id: "02", flag: "🇩🇪", name: "Germany" },
  { id: "03", flag: "🇮🇩", name: "Indonesia" },
  { id: "04", flag: "🇲🇾", name: "Malaysia" },
  { id: "05", flag: "🇦🇪", name: "UAE" },
  { id: "06", flag: "🇮🇷", name: "Iran" },
  { id: "07", flag: "🇮🇹", name: "Italy, Rome" },
  { id: "08", flag: "🇨🇭", name: "Switzerland" },
  { id: "09", flag: "🇳🇱", name: "Netherlands" },
  { id: "10", flag: "🇬🇧", name: "England" },
  { id: "11", flag: "🇹🇷", name: "Turkey" },
  { id: "12", flag: "🇧🇩", name: "Bangladesh" },
  { id: "13", flag: "🇲🇺", name: "Mauritius" },
  { id: "14", flag: "🇧🇪", name: "Belgium" },
];

const leftCol = countries.slice(0, 7);
const rightCol = countries.slice(7);

const subjects = [
  { room: "01", label: "Physics" },
  { room: "02", label: "Chemistry" },
  { room: "03", label: "Biology" },
  { room: "04", label: "Mathematics" },
  { room: "05", label: "Anatomy" },
  { room: "06", label: "Surface Anatomy" },
];

const hashtags = [
  "EducationTechnology",
  "DigitalLearning",
  "MedicalEducation",
  "VirtualLearning",
  "InnovationInEducation",
  "FutureOfLearning",
  "CBSE",
  "ICSE",
  "AnatomyEducation",
  "SkillDevelopment",
  "IndiaEducation",
  "GlobalLearning",
  "SaileshDani",
];

const GlobalEducationJourney = () => {
  return (
    <div className="bg-white text-[#14161A] font-['Inter',sans-serif]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .font-display { font-family: 'Space Grotesk', sans-serif; }
        .font-mono-tag { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* ===== RULER TOP BAR ===== */}
      <div className="flex items-center justify-between border-b border-[#14161A] px-6 md:px-10 lg:px-20 py-2">
        <span className="font-mono-tag text-[10px] tracking-[0.2em] text-[#6B7280] uppercase">
          Edu-Expedition / Sheet 01
        </span>
        <span className="font-mono-tag text-[10px] tracking-[0.2em] text-[#6B7280] uppercase hidden sm:inline">
          Scale — NTS
        </span>
      </div>

      {/* ===== HERO ===== */}
      <section className="px-3 md:px-7 lg:px-20 pt-16 pb-12 grid md:grid-cols-[1fr_260px] gap-10">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#2451D9]" />
            <span className="font-mono-tag text-xs tracking-[0.25em] text-[#2451D9] uppercase">
              Field Research → Platform Build
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05]">
            Exploring Global Education,
            <br />
            Transforming Indian Learning
          </h1>
          <p className="mt-6 max-w-xl text-[#4B5160] text-base md:text-lg leading-relaxed">
            With a vision to bridge the gap between students, educators, and
            modern teaching methodologies,{" "}
            <span className="text-[#14161A] font-medium">Sailesh Dani</span>{" "}
            has travelled across the world to study advanced education
            systems and innovative learning practices.
          </p>
        </div>

        {/* title-block spec sheet */}
        <div className="border border-[#14161A] h-fit">
          <SpecRow label="Scope" value="14 countries" />
          <SpecRow label="Levels" value="Class VIII–XII" />
          <SpecRow label="Curricula" value="CBSE / ICSE" />
          <SpecRow label="Subjects" value="6 modules" />
          <SpecRow label="Status" value="Phase 2 — Medical" last />
        </div>
      </section>

      {/* ===== COUNTRIES — ROUTE / WAYPOINTS ===== */}
      <section className="border-t border-[#14161A]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
          <SectionHeader index="A" label="Route — 14 Waypoints" />

          <div className="mt-10 grid md:grid-cols-2 gap-x-16">
            <WaypointColumn items={leftCol} />
            <WaypointColumn items={rightCol} />
          </div>
        </div>
      </section>

      {/* ===== THE PLATFORM — ROOM SCHEDULE ===== */}
      <section className="border-t border-[#14161A] bg-[#F5F7FB]">
        <div className="px-3 md:px-7 lg:px-20 py-16">
          <SectionHeader index="B" label="The Platform" />

          <p className="mt-6 max-w-2xl text-[#4B5160] leading-relaxed">
            These global experiences shaped a cutting-edge integrated
            education platform, built to strengthen learning outcomes and
            reduce student dropout rates — a complete workstation for
            students from Class VIII to XII, aligned with{" "}
            <span className="text-[#14161A] font-medium">CBSE</span> and{" "}
            <span className="text-[#14161A] font-medium">ICSE</span>{" "}
            curricula.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-px bg-[#14161A] border border-[#14161A]">
            {subjects.map((s) => (
              <div
                key={s.room}
                className="bg-white px-5 py-6 hover:bg-[#EEF2F8] transition-colors duration-200"
              >
                <div className="font-mono-tag text-[10px] tracking-widest text-[#2451D9] mb-2">
                  ROOM {s.room}
                </div>
                <div className="font-display text-lg">{s.label}</div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm text-[#6B7280] max-w-2xl leading-relaxed">
            Interactive learning, virtual education tools, assessments, and
            teacher support — combined on a single platform.
          </p>
        </div>
      </section>

      {/* ===== NEXT FRONTIER — PROPOSED ADDITION ===== */}
      <section className="border-t border-[#14161A]">
        <div className="px-3 md:px-7 lg:px-20 py-16">
          <SectionHeader index="C" label="Next Frontier" />

          <div className="mt-8 border-2 border-dashed border-[#E8A33D] p-6 md:p-8">
            <span className="font-mono-tag text-[10px] tracking-[0.25em] text-[#E8A33D] uppercase">
              Proposed Addition — Not Yet Built
            </span>
            <h3 className="font-display text-2xl md:text-3xl mt-3 mb-4">
              Medical Sciences Wing
            </h3>
            <p className="text-[#4B5160] leading-relaxed max-w-2xl mb-6">
              A unified digital ecosystem for the next generation of medical
              learners — empowering students, educators, and institutions
              with world-class learning technology under one platform.
            </p>
            <div className="flex flex-wrap gap-3">
              {["Anatomy", "Physiology", "Medical Sciences"].map((item) => (
                <span
                  key={item}
                  className="font-mono-tag text-xs border border-[#E8A33D] text-[#8A5A10] px-3 py-1"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CLOSING NOTE — MARGIN ANNOTATION ===== */}
      <section className="border-t border-[#14161A]">
        <div className="px-3 md:px-7 lg:px-20 py-16">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="hidden md:flex items-center pt-2">
              <span className="h-px w-10 bg-[#14161A]" />
            </div>
            <div className="border-l-2 border-[#14161A] pl-6">
              <span className="font-mono-tag text-[10px] tracking-[0.2em] text-[#6B7280] uppercase">
                Note
              </span>
              <p className="font-display text-xl md:text-2xl mt-2 leading-snug">
                "Education is not merely about learning facts; it is about
                creating opportunities, inspiring innovation, and shaping the
                future."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER — INDEX / TAGS ===== */}
      <footer className="border-t border-[#14161A] py-8">
        <div className="px-3 md:px-7 lg:px-20">
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {hashtags.map((tag, i) => (
              <span key={tag} className="font-mono-tag text-[11px] text-[#6B7280]">
                #{tag}
                {i < hashtags.length - 1 && (
                  <span className="text-[#D1D5DB] ml-4">/</span>
                )}
              </span>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between font-mono-tag text-[10px] text-[#9CA3AF] uppercase tracking-widest">
            <span>Sheet 01 of 01</span>
            <span>Rev. A</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

const SectionHeader = ({ index, label }) => (
  <div className="flex items-center gap-3">
    <span className="font-mono-tag text-xs text-[#2451D9] border border-[#2451D9] w-6 h-6 flex items-center justify-center">
      {index}
    </span>
    <h2 className="font-display text-2xl md:text-3xl">{label}</h2>
  </div>
);

const SpecRow = ({ label, value, last }) => (
  <div
    className={`flex justify-between items-center px-4 py-3 ${
      !last ? "border-b border-[#14161A]" : ""
    }`}
  >
    <span className="font-mono-tag text-[10px] tracking-widest text-[#6B7280] uppercase">
      {label}
    </span>
    <span className="font-mono-tag text-xs">{value}</span>
  </div>
);

const WaypointColumn = ({ items }) => (
  <div className="relative pl-8">
    <span className="absolute left-[7px] top-2 bottom-2 w-px bg-[#D1D5DB]" />
    <div className="flex flex-col gap-6">
      {items.map((c) => (
        <div key={c.id} className="relative flex items-center gap-4">
          <span className="absolute -left-8 w-3.5 h-3.5 rounded-full border-2 border-[#2451D9] bg-white" />
          <span className="font-mono-tag text-[10px] text-[#9CA3AF]">
            WP-{c.id}
          </span>
          <span className="text-xl leading-none">{c.flag}</span>
          <span className="font-display text-base">{c.name}</span>
        </div>
      ))}
    </div>
  </div>
);

export default GlobalEducationJourney;